import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import WithdrawalStatus from "../components/WithdrawalStatus";
import { RenewalNotice, PlanValidity } from "../components/RenewalNotice";
import { LaunchBanner } from "../components/LaunchBanner";
import { ConsumptionCard } from "../components/ConsumptionBalance";
import { brl } from "../components/PurchaseDialog";
import { BuyerHome } from "../components/BuyerHome";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Copy, Share2, Network, Users, Wallet, Sparkles, ArrowUpRight } from "lucide-react";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyLink = async () => {
    if (!stats?.referral_link) return;
    await navigator.clipboard.writeText(stats.referral_link);
    toast.success("Link copiado!");
  };

  const share = async () => {
    if (!stats?.referral_link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Conecta Mais", url: stats.referral_link });
      } catch (err) {
        // Ignore user-cancelled shares (AbortError); log unexpected errors.
        if (err?.name !== "AbortError") console.warn("Share failed:", err);
      }
    } else copyLink();
  };

  const upgrade = async (plan) => {
    try {
      await api.post("/plan/upgrade", { plan });
      toast.success("Pague o PIX de R$ 39,90 para ativar sua participação");
      await (typeof refresh === "function" ? refresh() : Promise.resolve());
      navigate("/aguardando-ativacao");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !stats) return <div className="pt-28 text-center text-muted-foreground">Carregando...</div>;
  if (stats.user.account_type === "buyer") return <BuyerHome u={stats.user} />;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Escritório Virtual</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">
              Olá, <span className="gold-text">{user.name.split(" ")[0]}</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="gold-btn">{stats.user.plan_label}</Badge>
            <Badge variant="outline" data-testid="dash-status">
              {stats.user.status === "active" ? "Ativo" : "Inativo"}
            </Badge>
          </div>
        </div>

        <LaunchBanner />
        <RenewalNotice user={stats.user} />
        <div className="h-6" />

        {/* Metric cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6 gold-border" data-testid="card-balance">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <Wallet className="h-4 w-4" /> Disponível para saque
            </div>
            <div className="mt-2 font-mono text-3xl font-bold gold-text" data-testid="withdrawable-balance">{brl(stats.user.balance)}</div>
            <div className="text-xs text-muted-foreground mt-1">Total ganho: {brl(stats.total_earned)}</div>
            <div className="text-xs mt-1 text-emerald-500" data-testid="card-balance-consumption">
              Saldo Consumo: {brl(stats.user.consumption_balance)}
            </div>
          </Card>
          <Card className="p-6" data-testid="card-direct">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <Users className="h-4 w-4" /> Indicações diretas
            </div>
            <div className="mt-2 font-mono text-3xl font-bold">{stats.direct_referrals}</div>
            <Button variant="link" className="px-0 h-auto text-xs gold-text" onClick={() => navigate("/indicacoes")}>
              Ver todas <ArrowUpRight className="h-3 w-3 ml-1" />
            </Button>
          </Card>
          <Card className="p-6" data-testid="card-matrix">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <Network className="h-4 w-4" /> Matriz total
            </div>
            <div className="mt-2 font-mono text-3xl font-bold">
              {stats.matrix_levels.reduce((a, l) => a + l.filled, 0)}
              <span className="text-sm text-muted-foreground">
                {" "}/ {stats.matrix_levels.reduce((a, l) => a + l.capacity, 0).toLocaleString("pt-BR")}
              </span>
            </div>
            <Button variant="link" className="px-0 h-auto text-xs gold-text" onClick={() => navigate("/matriz")}>
              Ver matriz <ArrowUpRight className="h-3 w-3 ml-1" />
            </Button>
          </Card>
          <Card className="p-6" data-testid="card-plan">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <Sparkles className="h-4 w-4" /> Plano
            </div>
            <div className="mt-2 font-display text-xl font-bold">{stats.user.plan_label}</div>
            <PlanValidity user={stats.user} />
          </Card>
        </div>

        {/* Bonifications breakdown (informativo / gerado / não qualificado) */}
        {stats.totals && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6" data-testid="bonus-breakdown">
            <Card className="card-hover p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Gerado (pago)</div>
              <div className="mt-1 font-mono text-2xl font-bold gold-text">{brl(stats.totals.paid)}</div>
              <div className="text-xs text-muted-foreground mt-1">80% no saldo de saque + 20% no Saldo Consumo</div>
            </Card>
            <Card className="card-hover p-5 border-amber-500/40 bg-amber-500/5" data-testid="card-informative-balance">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Saldo retido</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-600 dark:text-amber-400" data-testid="informative-not-withdrawable-badge">
                  Não sacável
                </span>
              </div>
              <div className="mt-1 font-mono text-2xl font-bold" data-testid="informative-balance-value">{brl(stats.totals.informative)}</div>
              <div className="text-xs text-muted-foreground mt-1" data-testid="next-closing">
                Comissões geradas enquanto você estava inativo. São liberadas no seu saldo assim que você ativar ou renovar o plano.
                Se a conta for excluída (2º mês sem renovar), o valor sobe para os ativos acima.
              </div>
            </Card>
            <Card className="card-hover p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Não qualificado</div>
              <div className="mt-1 font-mono text-2xl font-bold text-muted-foreground">
                {stats.totals.not_qualified_entries || 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Lançamentos acima da profundidade do seu plano</div>
            </Card>
          </div>
        )}

        {/* Referral link + Levels */}
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <Card className="p-6 lg:col-span-2">
            <WithdrawalStatus balance={stats.user.balance} />
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Seu link de indicação</div>
            <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-muted/60 font-mono text-sm break-all" data-testid="referral-link">
              {stats.referral_link}
            </div>
            <div className="mt-3 flex gap-2">
              <Button onClick={copyLink} className="gold-btn hover:opacity-95" data-testid="copy-referral-btn">
                <Copy className="h-4 w-4 mr-2" /> Copiar
              </Button>
              <Button variant="outline" onClick={share} data-testid="share-referral-btn">
                <Share2 className="h-4 w-4 mr-2" /> Compartilhar
              </Button>
            </div>
          </Card>

          <Card className="p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Progresso por nível</div>
            <div className="mt-4 space-y-3">
              {stats.matrix_levels.map((l) => (
                <div key={l.level}>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Nível {l.level}</span>
                    <span className="font-mono gold-text">{l.filled} / {l.capacity.toLocaleString("pt-BR")}</span>
                  </div>
                  <Progress value={(l.filled / l.capacity) * 100} className="h-2" />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Ledger */}
        <Card className="p-6 mt-6">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Histórico de bonificações</div>
          {stats.ledger.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4">Você ainda não recebeu bonificações. Compartilhe seu link e comece a construir sua rede.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left py-2">Indicado</th>
                    <th className="text-left">Data</th>
                    <th className="text-left">Nível</th>
                    <th className="text-right">Valor</th>
                  </tr>
                </thead>
                <tbody data-testid="ledger-table">
                  {stats.ledger.map((l) => (
                    <tr key={l.id} className="border-b border-border/50">
                      <td className="py-2 font-medium">{l.from_user_name || "—"}</td>
                      <td className="text-muted-foreground">{new Date(l.created_at).toLocaleDateString("pt-BR")}</td>
                      <td>Nível {l.level}</td>
                      <td className="text-right">
                        {l.amount > 0 ? (
                          <span className="font-mono gold-text">{brl(l.amount)}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic" title={l.reason || ""}>
                            Sem bonificação
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <ConsumptionCard />
      </div>
    </div>
  );
}
